import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  Download,
  ExternalLink,
  Loader2,
  Maximize2,
  Minimize2,
  RotateCw,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

import { cn } from "@/lib/utils";

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 8;
const ZOOM_STEP = 0.25;

const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

/**
 * Full-screen image viewer for ticket attachments.
 *
 * Support tickets are almost always screenshots of a bug, so the old inline
 * `max-h-[60dvh]` preview inside an already-constrained modal made the text in
 * them unreadable. This gives the actual size: zoom, pan, rotate, open and
 * download.
 *
 * Sits at z-[60] so it clears the ticket modal (z-50) it is opened from, and
 * swallows Escape in the capture phase so closing the viewer does not also
 * close that modal underneath it.
 */
export default function ImageLightbox({
  src,
  alt = "Attachment",
  caption,
  onClose,
}: {
  src: string;
  alt?: string;
  caption?: string;
  onClose: () => void;
}) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [downloading, setDownloading] = useState(false);

  // The ref carries the drag maths (no re-render per mousemove); the state flag
  // is what the cursor and the transform transition read, since a ref change
  // would not re-render and panning would animate at 140ms and feel laggy.
  const dragState = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);

  const reset = useCallback(() => {
    setZoom(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
  }, []);

  const zoomBy = useCallback((delta: number) => {
    setZoom((z) => {
      const next = clampZoom(z + delta);
      // Snapping back to fit should re-centre, otherwise the image can be
      // parked off-screen with no way to find it again.
      if (next <= 1) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  // Escape must close the viewer WITHOUT closing the ticket modal behind it.
  // Both listen on document, so capture + stopImmediatePropagation is what
  // keeps the parent's handler from ever seeing the key.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "+" || e.key === "=") { e.preventDefault(); zoomBy(ZOOM_STEP); }
      else if (e.key === "-" || e.key === "_") { e.preventDefault(); zoomBy(-ZOOM_STEP); }
      else if (e.key === "0") { e.preventDefault(); reset(); }
      else if (e.key === "r" || e.key === "R") { e.preventDefault(); setRotation((r) => (r + 90) % 360); }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose, zoomBy, reset]);

  // Wheel zoom. Non-passive so preventDefault actually stops the page behind
  // it from scrolling.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      zoomBy(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
    }
    stage.addEventListener("wheel", onWheel, { passive: false });
    return () => stage.removeEventListener("wheel", onWheel);
  }, [zoomBy]);

  const startDrag = (e: React.MouseEvent) => {
    if (zoom <= 1) return;
    e.preventDefault();
    dragState.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    setDragging(true);
  };

  useEffect(() => {
    function onMove(e: MouseEvent) {
      const d = dragState.current;
      if (!d) return;
      setOffset({ x: d.ox + (e.clientX - d.x), y: d.oy + (e.clientY - d.y) });
    }
    function onUp() {
      if (!dragState.current) return;
      dragState.current = null;
      setDragging(false);
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // The blob store is a different origin, so <a download> is ignored there —
  // fetch the bytes and save them locally instead, falling back to a new tab.
  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(src, { mode: "cors" });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = src.split("/").pop()?.split("?")[0] || "attachment.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(src, "_blank", "noopener,noreferrer");
    } finally {
      setDownloading(false);
    }
  };

  const atActualSize = Math.abs(zoom - 1) < 0.01 && rotation === 0 && offset.x === 0 && offset.y === 0;

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-black/90 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Toolbar */}
      <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-3 bg-black/60 border-b border-white/10">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate">{caption ?? alt}</p>
          <p className="text-[11px] text-white/50">
            {natural ? `${natural.w} × ${natural.h}px · ` : ""}
            {Math.round(zoom * 100)}%
            <span className="hidden sm:inline"> · scroll to zoom, drag to pan, Esc to close</span>
          </p>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <ToolButton onClick={() => zoomBy(-ZOOM_STEP)} disabled={zoom <= MIN_ZOOM} label="Zoom out (−)">
            <ZoomOut className="h-4 w-4" />
          </ToolButton>
          <ToolButton onClick={() => zoomBy(ZOOM_STEP)} disabled={zoom >= MAX_ZOOM} label="Zoom in (+)">
            <ZoomIn className="h-4 w-4" />
          </ToolButton>
          <ToolButton
            onClick={() => (atActualSize ? setZoom(2) : reset())}
            label={atActualSize ? "Zoom to 200%" : "Reset to fit (0)"}
          >
            {atActualSize ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
          </ToolButton>
          <ToolButton onClick={() => setRotation((r) => (r + 90) % 360)} label="Rotate 90° (R)">
            <RotateCw className="h-4 w-4" />
          </ToolButton>

          <span className="mx-1 h-5 w-px bg-white/15" />

          <ToolButton onClick={handleDownload} disabled={downloading} label="Download">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          </ToolButton>
          <ToolButton onClick={() => window.open(src, "_blank", "noopener,noreferrer")} label="Open in new tab">
            <ExternalLink className="h-4 w-4" />
          </ToolButton>
          <ToolButton onClick={onClose} label="Close (Esc)">
            <X className="h-4 w-4" />
          </ToolButton>
        </div>
      </div>

      {/* Stage — no vh units: the app-wide zoom on <html> makes those unreliable,
          so this fills the remaining space with flex instead. */}
      <div
        ref={stageRef}
        className={cn(
          "relative flex-1 min-h-0 overflow-hidden flex items-center justify-center p-4 select-none",
          zoom > 1 ? (dragging ? "cursor-grabbing" : "cursor-grab") : "cursor-default",
        )}
        onMouseDown={startDrag}
        onDoubleClick={() => (atActualSize ? setZoom(2) : reset())}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-white/60" />
          </div>
        )}

        {status === "error" ? (
          <div className="flex flex-col items-center gap-3 text-center px-6">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10">
              <AlertCircle className="h-6 w-6 text-white/70" />
            </div>
            <p className="text-sm font-semibold text-white">This image could not be loaded</p>
            <p className="text-xs text-white/60 max-w-xs">
              The file may have been removed from storage, or the link has expired.
            </p>
            <button
              onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
              className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/20 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Try opening directly
            </button>
          </div>
        ) : (
          <img
            src={src}
            alt={alt}
            draggable={false}
            onLoad={(e) => {
              setStatus("ready");
              setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
            }}
            onError={() => setStatus("error")}
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
              transition: dragging ? "none" : "transform 140ms ease-out",
            }}
            className={cn(
              "max-h-full max-w-full object-contain rounded-lg shadow-2xl",
              status === "loading" && "opacity-0",
            )}
          />
        )}
      </div>
    </div>
  );
}

function ToolButton({
  onClick,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-white/80 hover:bg-white/15 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
    >
      {children}
    </button>
  );
}
